import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";

/**
 * ResponseCounter - A shared component that displays the user's response count
 * Shows loading state when switching workspaces to prevent displaying stale data
 * 
 * @param {boolean} showUpgradeButton - Whether to show the upgrade button
 * @param {string} variant - 'compact' for sidebar, 'full' for profile menu
 */
const ResponseCounter = ({ showUpgradeButton = true, variant = "full" }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { totalResponses, currentSubscription, usageLimits } = useSelector((state) => state.app);
  const counterCardClass = "bg-surface-subtle border border-line-subtle";
  
  const [displayedValue, setDisplayedValue] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const workspaceIdRef = useRef(null);
  const previousResponsesRef = useRef(null);
  const workspaceChangeTimestampRef = useRef(null);
  const fallbackTimeoutRef = useRef(null);

  // Extract workspace ID from URL
  const currentWorkspaceId = new URLSearchParams(location.search).get('w');

  // Detect workspace change
  useEffect(() => {
    // If workspace changed (but not initial mount)
    if (currentWorkspaceId !== workspaceIdRef.current) {
      const isInitialMount = workspaceIdRef.current === null;
      
      workspaceIdRef.current = currentWorkspaceId;
      
      // Only reset state if this is NOT the initial mount
      if (!isInitialMount) {
        workspaceChangeTimestampRef.current = Date.now();
        
        // Show loading state immediately - NEVER show old value
        setIsLoading(true);
        setDisplayedValue(null);
        
        // Clear existing timeout
        if (fallbackTimeoutRef.current) {
          clearTimeout(fallbackTimeoutRef.current);
          fallbackTimeoutRef.current = null;
        }
      }
    }
  }, [currentWorkspaceId]);

  // Handle totalResponses updates
  useEffect(() => {
    if (totalResponses === undefined || totalResponses === null) {
      return;
    }

    // Initial load - show data immediately
    if (workspaceChangeTimestampRef.current === null) {
      setDisplayedValue(totalResponses);
      setIsLoading(false);
      previousResponsesRef.current = totalResponses;
      workspaceChangeTimestampRef.current = Date.now();
      return;
    }

    const timeSinceChange = Date.now() - workspaceChangeTimestampRef.current;
    
    // CRITICAL: Ignore values that come too fast (< 300ms) after workspace change
    // These are stale values from Redux store before NavBar updated it
    // BUT: Allow updates if the previous value was 0 or null (initial state)
    if (timeSinceChange < 300 && previousResponsesRef.current !== 0 && previousResponsesRef.current !== null) {
      return;
    }
    
    // Only accept DIFFERENT values after workspace change
    if (previousResponsesRef.current !== totalResponses) {
      setDisplayedValue(totalResponses);
      setIsLoading(false);
      previousResponsesRef.current = totalResponses;
    }
  }, [totalResponses]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current);
      }
    };
  }, []);

  const handleUpgradeClick = () => {
    navigate('/profile?tab=billing');
  };

  const planType = currentSubscription?.plan?.type || "free";
  const responseLimit =
    usageLimits?.responses ?? currentSubscription?.responseLimit;
  const isUnlimited = responseLimit === null || responseLimit === undefined;

  // Calculate progress bar percentage
  const percentage = !isUnlimited && displayedValue !== null && responseLimit > 0
    ? Math.min((displayedValue / responseLimit) * 100, 100)
    : 0;

  // Determine progress bar color
  const getProgressBarColor = () => {
    if (!isUnlimited && displayedValue >= responseLimit) return "bg-red-500";
    if (percentage >= 80) return "bg-yellow-500";
    return "bg-brand-primary";
  };

  // Calculate reset date for monthly plans
  const getResetText = () => {
    const lastUpdate = currentSubscription?.responseLimitLastUpdateDate;
    if (!lastUpdate) return "Resets in -- days";
    
    const now = new Date();
    const lastUpdateDate = new Date(lastUpdate);
    const nextReset = new Date(lastUpdateDate);
    nextReset.setDate(nextReset.getDate() + 30);
    
    const diffTime = nextReset - now;
    if (diffTime <= 0) return "Resets today";
    
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return `Resets in ${diffDays === 1 ? "1 day" : `${diffDays} days`}`;
  };

  if (variant === "compact") {
    // Compact version for sidebar
    return (
      <div className={`my-[8px] px-4 py-3 rounded-xl ${counterCardClass}`}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-ink-primary">
            {planType.charAt(0).toUpperCase() + planType.slice(1)} plan
          </span>
          {showUpgradeButton && (
            <button
              onClick={handleUpgradeClick}
              className="text-xs font-medium text-brand-primary hover:text-brand-primary-hover"
            >
              Upgrade
            </button>
          )}
        </div>
        <div className="text-xs font-medium text-ink-subtle mb-2">
          Monthly response limit
        </div>
        <div className="flex items-baseline mb-2">
          {isLoading ? (
            <span className="text-lg font-bold text-ink-disabled animate-pulse">
              --
            </span>
          ) : (
            <span className="text-lg font-bold text-ink-primary">
              {displayedValue}
            </span>
          )}
          <span className="text-sm font-medium text-ink-subtle ml-1">
            / {isUnlimited ? "∞" : responseLimit}
          </span>
        </div>
        <div className="w-full h-1.5 bg-line rounded-full overflow-hidden mb-1">
          {!isLoading && displayedValue !== null ? (
            <div
              className={`h-full rounded-full transition-all duration-300 ${getProgressBarColor()}`}
              style={{ width: `${isUnlimited ? 0 : percentage}%` }}
            />
          ) : (
            <div 
              className="h-full bg-line-strong rounded-full animate-pulse" 
              style={{ width: '20%' }} 
            />
          )}
        </div>
        <div className="text-xs font-medium text-ink-disabled">
          {getResetText()}
        </div>
      </div>
    );
  }

  // Full version for profile menu (same as compact for now)
  return (
    <div className={`mx-3 my-2 px-4 py-3 rounded-xl ${counterCardClass}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-ink-primary">
          {planType.charAt(0).toUpperCase() + planType.slice(1)} plan
        </span>
        {showUpgradeButton && (
          <button
            onClick={handleUpgradeClick}
            className="text-xs font-medium text-brand-primary hover:text-brand-primary-hover"
          >
            Upgrade
          </button>
        )}
      </div>
      <div className="text-xs font-medium text-ink-subtle mb-2">
        Monthly response limit
      </div>
      <div className="flex items-baseline mb-2">
        {isLoading ? (
          <span className="text-lg font-bold text-ink-disabled animate-pulse">
            --
          </span>
        ) : (
          <span className="text-lg font-bold text-ink-primary">
            {displayedValue}
          </span>
        )}
        <span className="text-sm font-medium text-ink-subtle ml-1">
          / {isUnlimited ? "∞" : responseLimit}
        </span>
      </div>
      <div className="w-full h-1.5 bg-line rounded-full overflow-hidden mb-1">
        {!isLoading && displayedValue !== null ? (
          <div
            className={`h-full rounded-full transition-all duration-300 ${getProgressBarColor()}`}
            style={{ width: `${percentage}%` }}
          />
        ) : (
          <div 
            className="h-full bg-line-strong rounded-full animate-pulse" 
            style={{ width: '20%' }} 
          />
        )}
      </div>
      <div className="text-xs font-medium text-ink-disabled">
        {getResetText()}
      </div>
    </div>
  );
};

export default ResponseCounter;

